import React, { ReactElement, useState, useRef, cloneElement, ElementType, forwardRef, ComponentPropsWithoutRef } from 'react';
import { cx } from '@emotion/css';
import { Spinner } from '../spinner/spinner';
import { ContextMenuProps } from './context-menu/context-menu';
import { useOutsideHandler } from '@phoenix/design-system/frontend/util/use-outside-handler';
import { useFlags } from 'launchdarkly-react-client-sdk';

export type ButtonType =
  | 'white_default'
  | 'white_highlight'
  | 'white_outline'
  | 'white_text'
  | 'white_outline_opaque'
  | 'orange_default'
  | 'orange_outline'
  | 'orange_text'
  | 'grey_default'
  | 'grey_outline'
  | 'grey_text';

export type ButtonProps<C extends ElementType> = {
  as?: C;
  btnType: ButtonType;
  icon?: string;
  iconPosition?: 'left' | 'right';
  loader?: boolean;
  menu?: ReactElement<ContextMenuProps>;
  type?: 'button' | 'submit' | 'reset';
  size?: 'small' | 'regular';
  containerClassName?: string;
} & ComponentPropsWithoutRef<C>;

export type PolymorphicRef<C extends React.ElementType> = React.ComponentPropsWithRef<C>['ref'];

export const Button = forwardRef(
  <C extends React.ElementType = 'button'>(
    {
      as,
      children,
      btnType,
      type = 'button',
      icon,
      iconPosition = 'right',
      disabled = false,
      loader,
      menu,
      size = 'regular',
      containerClassName,
      ...otherProps
    }: ButtonProps<C>,
    ref?: PolymorphicRef<C>
  ) => {
    const Component = as || 'button';
    const typeClassNames: string[] = ['focus-visible:outline-primary-black'];
    let loaderClassNames = '';
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef(null);

    /* ENABLE the flag full-width-btn-on-small-screens in LD to enable the behavior on the app level */
    const { 'full-width-btn-on-small-screens': fullWidthBtnOnSmallScreens } = useFlags();

    switch (btnType) {
      case 'white_default':
        typeClassNames.push('bg-secondary-deep', 'border-secondary-deep', 'text-primary-white', 'border');

        !loader &&
          typeClassNames.push(
            // disabled
            'disabled:bg-secondary-mid',
            'disabled:border-secondary-mid',
            'disabled:text-secondary',
            // hover
            'hover:bg-secondary-dark',
            'hover:border-secondary-dark'
          );
        loaderClassNames = 'border-primary-white border-l-transparent';
        break;

      case 'white_highlight':
        typeClassNames.push('bg-primary-orange', 'text-secondary-deep', 'border-primary-orange', 'border');

        !loader &&
          typeClassNames.push(
            // disabled
            'disabled:bg-secondary-mid',
            'disabled:border-secondary-mid',
            'disabled:text-secondary',
            // hover
            'hover:bg-primary-orange-dark',
            'hover:border-primary-orange-dark'
          );
        loaderClassNames = 'border-secondary-deep border-l-transparent';
        break;

      case 'white_outline':
        typeClassNames.push('bg-transparent', 'text-secondary-deep', 'border-secondary-deep', 'border');

        !loader &&
          typeClassNames.push(
            // disabled
            'disabled:border-secondary',
            'disabled:text-secondary',
            // hover
            'hover:bg-secondary-light'
          );
        loaderClassNames = 'border-secondary-deep border-l-transparent';
        break;

      case 'white_outline_opaque':
        typeClassNames.push('bg-primary-white', 'text-secondary-deep', 'border-secondary-deep', 'border');

        !loader &&
          typeClassNames.push(
            // disabled
            'disabled:border-secondary',
            'disabled:text-secondary',
            // hover
            'hover:enabled:bg-secondary-light'
          );
        loaderClassNames = 'border-secondary-deep border-l-transparent';
        break;

      case 'grey_text':
      case 'white_text':
        typeClassNames.push('bg-transparent', 'text-secondary-deep');
        !loader &&
          typeClassNames.push(
            // disabled
            'disabled:text-secondary',
            // hover
            'hover:underline'
          );
        loaderClassNames = `border-secondary-deep border-l-transparent`;
        break;

      case 'orange_default':
        typeClassNames.push('bg-primary-white', 'text-secondary-deep', 'border-primary-white', 'border');

        !loader &&
          typeClassNames.push(
            // disabled
            'disabled:bg-primary-white',
            'disabled:text-secondary',
            // hover
            'hover:bg-secondary-light',
            'hover:border-secondary-light'
          );
        loaderClassNames = 'border-secondary-deep border-l-transparent text-secondary';
        break;

      case 'orange_outline':
        typeClassNames.push('bg-transparent', 'text-secondary-deep', 'border-secondary-deep', 'border');

        !loader &&
          typeClassNames.push(
            // disabled
            'disabled:text-secondary-dark',
            'disabled:border-secondary-dark',
            // hover
            'hover:bg-primary-orange-mid'
          );
        loaderClassNames = 'border-secondary-deep border-l-transparent';
        break;

      case 'grey_outline':
        typeClassNames.push('bg-transparent', 'text-secondary-deep', 'border-secondary-deep', 'border');

        !loader &&
          typeClassNames.push(
            // disabled
            'disabled:text-secondary',
            'disabled:border-secondary',
            // hover
            'hover:bg-secondary-mid'
          );
        loaderClassNames = 'border-secondary-deep border-l-transparent';
        break;

      case 'orange_text':
        typeClassNames.push('bg-transparent', 'text-secondary-deep');
        !loader &&
          typeClassNames.push(
            // disabled
            'disabled:text-secondary-dark',
            // hover
            'hover:underline'
          );
        loaderClassNames = 'border-secondary-deep border-l-transparent';
        break;

      case 'grey_default':
      default:
        // white_default, grey default
        typeClassNames.push('bg-secondary-deep', 'border-secondary-deep', 'text-primary-white', 'border');

        !loader &&
          typeClassNames.push(
            // disabled
            'disabled:bg-secondary',
            'disabled:border-secondary',
            // hover
            'hover:bg-secondary-dark',
            'hover:border-secondary-dark'
          );
        loaderClassNames = 'border-primary-white border-l-transparent';
    }

    const getIconFilter = () => {
      switch (btnType) {
        case 'white_default':
          return disabled ? 'invert(77%) sepia(0%) saturate(362%) hue-rotate(243deg) brightness(94%) contrast(87%)' : 'invert(100%)';
        case 'white_highlight':
        case 'white_outline':
        case 'white_outline_opaque':
          return disabled
            ? 'invert(77%) sepia(0%) saturate(362%) hue-rotate(243deg) brightness(94%) contrast(87%)'
            : 'invert(9%) sepia(3%) saturate(20%) hue-rotate(328deg) brightness(95%) contrast(86%)';
        case 'grey_default':
          return 'invert(91%) sepia(100%) saturate(34%) hue-rotate(197deg) brightness(107%) contrast(99%)';
        case 'white_text':
        case 'grey_text':
          return disabled ? 'invert(85%) sepia(0%) saturate(1969%) hue-rotate(103deg) brightness(93%) contrast(80%)' : '';
        case 'grey_outline':
          return disabled
            ? 'invert(85%) sepia(0%) saturate(1969%) hue-rotate(103deg) brightness(93%) contrast(80%)'
            : 'invert(8%) sepia(2%) saturate(0%) hue-rotate(342deg) brightness(102%) contrast(86%)';

        case 'orange_default':
          return disabled
            ? 'invert(85%) sepia(0%) saturate(1969%) hue-rotate(103deg) brightness(93%) contrast(80%)'
            : 'invert(11%) sepia(0%) saturate(0%) hue-rotate(219deg) brightness(100%) contrast(90%)';

        case 'orange_outline':
          return disabled
            ? 'invert(30%) sepia(5%) saturate(15%) hue-rotate(32deg) brightness(89%) contrast(82%)'
            : 'invert(9%) sepia(3%) saturate(20%) hue-rotate(328deg) brightness(95%) contrast(86%)';
        case 'orange_text':
          return disabled ? 'invert(30%) sepia(5%) saturate(15%) hue-rotate(32deg) brightness(89%) contrast(82%)' : '';
        default:
          break;
      }
    };

    const renderIcon = () => {
      return (
        <img
          src={icon}
          alt=""
          height="4"
          width="4"
          className={`inline-block h-4 w-4 ${!!children && iconPosition === 'left' ? 'mr-2' : !!children && 'ml-2'}`}
          style={{
            filter: getIconFilter(),
          }}
        />
      );
    };

    const renderLoader = () => {
      return <Spinner className={cx('-mb-1 ml-2 inline-block h-4 w-4 rounded-half border-[0.15rem] after:w-4', loaderClassNames)} />;
    };

    const renderContent = () => {
      return (
        <>
          {icon && iconPosition === 'left' && renderIcon()}
          {children}
          {icon && iconPosition === 'right' && renderIcon()}
          {loader && renderLoader()}
        </>
      );
    };

    const isTextBtnType = () => btnType.includes('_text');

    const className = cx(
      typeClassNames.join(' '),
      'stihl-text-interactive-button flex items-center justify-center',
      !isTextBtnType() && size === 'regular' && `min-h-[3rem] py-2.5 ${children ? 'px-6' : 'px-4'}`,
      !isTextBtnType() && size === 'small' && `min-h-[2rem] py-1 ${children ? 'px-6' : 'px-4'}`,
      otherProps.className,
      fullWidthBtnOnSmallScreens && 'w-full desktop:w-auto',
      disabled || loader ? 'cursor-not-allowed' : 'cursor-pointer'
    );

    const commonProps = {
      type: otherProps?.href ? undefined : type,
      className,
      disabled,
    };

    const _onClick = () => {
      setShowMenu(!showMenu);
    };

    useOutsideHandler(menuRef, () => {
      setShowMenu(false);
    });

    return (
      <div className={cx('relative', containerClassName)} onClick={_onClick} ref={menuRef}>
        <Component {...otherProps} {...commonProps} ref={ref}>
          {renderContent()}
        </Component>
        {menu && cloneElement(menu, { show: showMenu })}
      </div>
    );
  }
);
